import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export type VignetteStatus = 'valid' | 'expiring' | 'expired';

export interface Vignette {
  id: string;
  vehicleId: string;
  country: string;
  validFrom: string | null;
  validUntil: string;
  daysLeft: number;
  status: VignetteStatus;
  reference: string | null;
  amount: number | null;
  ccy: string | null;
  documentId: string | null;
  notes: string | null;
}

export interface VignetteMatrix {
  countries: string[];
  today: string;
  warnDays: number;
  vehicles: Array<{ id: string; fleetNo: string; plate: string; status: string; cells: Record<string, Vignette | null> }>;
  summary: { missing: number; expired: number; expiring: number; valid: number };
}

export function useVignettes() {
  return useQuery({ queryKey: ['vignettes', 'matrix'], queryFn: () => api.get<VignetteMatrix>('/vignettes') });
}

export function useVignetteHistory(vehicleId: string | undefined) {
  return useQuery({
    queryKey: ['vignettes', 'history', vehicleId],
    queryFn: () => api.get<Vignette[]>(`/vignettes/vehicles/${vehicleId}`),
    enabled: Boolean(vehicleId),
  });
}

function useVignetteMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vignettes'] });
      void qc.invalidateQueries({ queryKey: ['anomalies'] });
    },
  });
}

export interface VignetteInput {
  vehicleId: string;
  country: string;
  validFrom?: string | null;
  validUntil: string;
  reference?: string | null;
  amount?: number | null;
  ccy?: string;
  documentId?: string | null;
  notes?: string | null;
}

export function useSaveVignette() {
  return useVignetteMutation((input: { id?: string; body: VignetteInput }) => {
    if (input.id) {
      const { vehicleId: _v, country: _c, ...rest } = input.body;
      return api.patch<Vignette>(`/vignettes/${input.id}`, rest);
    }
    return api.post<Vignette>('/vignettes', input.body);
  });
}

export function useDeleteVignette() {
  return useVignetteMutation((id: string) => api.del<{ id: string }>(`/vignettes/${id}`));
}

export function useSetVignetteCountries() {
  return useVignetteMutation((countries: string[]) => api.put<{ countries: string[] }>('/vignettes/countries', { countries }));
}
